import { ChangeEvent, useEffect, useState } from 'react';
import * as S from './EnhancedTimeline.styled';
import {
  AlertSnackbar,
  AnomalyAPI,
  convertMillisecondsToSeconds,
  Nullable,
  RegionAPI,
  timelineLabel,
} from '@shared';
import { EnhancedTimelinePropsType, Tick } from './EnhancedTimeline.types';
import { anomaliesByOriginType, getAnomalyPosition, getTicks } from './EnhancedTimeline.utils';
import ModeStandbyIcon from '@mui/icons-material/ModeStandby';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import MonitorOutlinedIcon from '@mui/icons-material/MonitorOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';

export const EnhancedTimeline = (props: EnhancedTimelinePropsType) => {
  const {
    progressPlayed,
    onSeekChange,
    targetRegions,
    videoDuration,
    anomalies,
    allowedProgressRegions,
    isPlaying,
  } = props;

  const [ticks, setTicks] = useState<Tick[]>([]);
  const [error, setError] = useState<Nullable<unknown>>(null);

  const cameraAnomalies = anomaliesByOriginType(anomalies, 'camera');
  const screenAnomalies = anomaliesByOriginType(anomalies, 'screen');
  const soundAnomalies = anomaliesByOriginType(anomalies, 'sound');

  useEffect(() => {
    try {
      const receivedTicks = getTicks(videoDuration);
      setTicks(receivedTicks);
    } catch (exceptionError) {
      setError(exceptionError);
    }
  }, [videoDuration]);

  const handleTimelineChange = (e: ChangeEvent<HTMLInputElement>) => {
    const progress = Number(e.target.value);
    if (allowedProgressRegions) {
      allowedProgressRegions.forEach(({ start, end }) => {
        if (progress >= start && progress <= end) {
          onSeekChange(progress, isPlaying);
        }
      });
    } else {
      onSeekChange(progress, isPlaying);
    }
  };

  const renderRegion = (region: RegionAPI) => {
    const { id, start, end } = region;
    const { left, width } = getAnomalyPosition(videoDuration, start, end);

    return (
      <S.Region
        // onClick={() => onSeekChange(left / 100, true)}
        key={id}
        style={{ left: `${left}%`, width: `${width}%` }}
      />
    );
  };

  const renderAnomaly = (anomaly: AnomalyAPI) => {
    const { id, startOffset, endOffset } = anomaly;
    const { left, width } = getAnomalyPosition(videoDuration, startOffset, endOffset, true);

    return (
      <S.Anomaly
        onClick={() => onSeekChange(left / 100, isPlaying)}
        key={id}
        style={{ left: `${left}%`, width: `${width}%` }}
      />
    );
  };

  const renderTicks = () => (
    <S.Ticks>
      {ticks.map(({ id, timestamp, left }) => {
        return (
          <S.Tick key={id} style={{ left: `calc(${left}% - 24px)` }}>
            {timelineLabel(convertMillisecondsToSeconds(timestamp))}
          </S.Tick>
        );
      })}
    </S.Ticks>
  );

  const renderScheme = () => {
    return (
      <S.SchemeContainer id='scheme-container'>
        <S.Track type='alert'>
          <S.IconWrapper small>
            <ModeStandbyIcon fontSize='small' sx={S.IconStyles} />
          </S.IconWrapper>
          <S.AnomaliesWrapper>
            {targetRegions?.map((region) => renderRegion(region))}
          </S.AnomaliesWrapper>
          {/* <div onClick={handleClickRegion(targetRegions)} role='presentation' className={styles.target} />*/}
        </S.Track>
        <S.Track type='camera'>
          <S.IconWrapper>
            <CameraAltOutlinedIcon fontSize='small' sx={S.IconStyles} />
          </S.IconWrapper>
          {cameraAnomalies?.map((anomaly) => renderAnomaly(anomaly))}
          {/* <div onClick={handleClickAnomaly} role='presentation' className={styles.target} />*/}
        </S.Track>
        <S.Track type='screen'>
          <S.IconWrapper>
            <MonitorOutlinedIcon fontSize='small' sx={S.IconStyles} />
          </S.IconWrapper>
          {screenAnomalies?.map((anomaly) => renderAnomaly(anomaly))}
          {/* <div onClick={handleClickAnomaly} role='presentation' className={styles.target} />*/}
        </S.Track>
        <S.Track type='sound'>
          <S.IconWrapper>
            <VolumeUpOutlinedIcon fontSize='small' sx={S.IconStyles} />
          </S.IconWrapper>
          {soundAnomalies?.map((anomaly) => renderAnomaly(anomaly))}
          {/* <div onClick={handleClickAudioLevels} role='presentation' className={styles.target} />*/}
        </S.Track>
        {/* {hasSuspectRegions && (
          <div className={styles.warnings}>
            <div className={clsx(styles.iconWrapper, styles.iconSmWrapper)}>
              <ErrorOutlineOutlinedIcon fontSize='small' className={styles.icon} />
            </div>
            <div className={styles.anomalies}>
              {suspectRegions?.map((item) => renderRegion(item))}
            </div>
            <div
              onClick={handleClickRegion(suspectRegions)}
              role='presentation'
              className={styles.target}
            />
          </div>
        )}*/}
      </S.SchemeContainer>
    );
  };

  return (
    <>
      <AlertSnackbar
        text={`Something went wrong! This video is not available. ${
          error instanceof Error && error.message
        }`}
        open={!!error}
        handleClose={() => setError(null)}
      />
      <S.Root>
        <S.Scheme>
          {renderTicks()}
          {renderScheme()}
        </S.Scheme>
        <S.TimelineWrapper>
          <S.Timeline
            type='range'
            min={0}
            max={1}
            step='0.00001'
            value={progressPlayed}
            onChange={handleTimelineChange}
          />
        </S.TimelineWrapper>
      </S.Root>
    </>
  );
};
